import useAnalyticsCalls from '@/hooks/use-analytics';
import { useFunnelLead } from '@/hooks/useFunnelLead';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { JoinFunnelLayout } from '../JoinFunnelLayout';
import { FunnelChecklistCard } from '../components/FunnelChecklistCard';
import { FunnelField } from '../components/FunnelField';
import { FUNNEL_COPY } from '../funnel-copy';
import { stepRoutePath, useFunnelVariant } from '../routes';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

export function JoinStep1() {
  const variant = useFunnelVariant();
  const copy = FUNNEL_COPY[variant].step1;
  const navigate = useNavigate();
  const { lead, updateLead } = useFunnelLead();
  const analytics = useAnalyticsCalls();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);

  // Hydrate from server lead exactly once when it lands.
  const hasHydratedRef = useRef(false);
  useEffect(() => {
    if (!lead || hasHydratedRef.current) return;
    hasHydratedRef.current = true;
    setEmail(lead.email ?? '');
  }, [lead]);

  // Onboarding - Started (Meta view-002 per Tracking Requirements) fires on
  // first mount only. The per-step page view is handled globally by
  // useAnalyticsPageViews, which now attaches `positioning_variant` and
  // `funnel_step` to `[Amplitude] Page Viewed` for /join routes.
  const hasFiredOnceRef = useRef(false);
  useEffect(() => {
    if (hasFiredOnceRef.current) return;
    hasFiredOnceRef.current = true;
    analytics.callOnboardingStarted();
  }, [analytics]);

  const handleEmailBlur = async (value: string) => {
    if (!value) return;
    if (!EMAIL_REGEX.test(value)) {
      setEmailError('Enter a valid email address.');
      return;
    }
    setEmailError(null);
    try {
      await updateLead({ email: value });
      analytics.callFunnelFieldCaptured({ field: 'email', value, positioningVariant: variant });
    } catch {
      // Best-effort; FunnelGuard surfaces the failure and lazy-mint retries.
    }
  };

  const isComplete = email.trim() && !emailError;

  const handleContinue = () => {
    if (!isComplete) return;
    navigate(stepRoutePath(variant, 2));
  };

  return (
    <JoinFunnelLayout step={1} onContinue={handleContinue} isContinueDisabled={!isComplete}>
      <div className="flex flex-col gap-3">
        <h1 className="text-catch-dark-blue-90 text-3xl leading-tight font-bold">{copy.header}</h1>
        <p className="text-base text-gray-500">{copy.subheader}</p>
      </div>

      <FunnelChecklistCard sections={copy.checklist} />

      <div className="flex flex-col gap-4">
        <FunnelField
          id="email"
          label="Email"
          type="email"
          placeholder="you@example.com"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          onBlur={(e) => handleEmailBlur(e.target.value.trim())}
          autoComplete="email"
          error={emailError}
        />
      </div>
    </JoinFunnelLayout>
  );
}

export default JoinStep1;
